import React, {Suspense} from 'react';
import {ActivityIndicator, View} from 'react-native';

/**
 * Card-sized, transparent Suspense fallback for a lazily-loaded screen.
 *
 * During a stack transition React Navigation keeps the PREVIOUS card mounted,
 * so when a per-screen boundary catches the incoming screen's suspense only
 * that card shows this small spinner — the app no longer replaces the whole
 * tree with the opaque full-screen orange loader. The background is transparent
 * on purpose so the destination card never flashes a solid colour before its
 * first paint.
 */
const ScreenFallback = () => (
    <View style={{flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent'}}>
        <ActivityIndicator size="large" color="#F97316" />
    </View>
);

/**
 * Wrap a dynamic screen import in `React.lazy` AND its own `<Suspense>`
 * boundary. Every screen in the navigator is code-split with `React.lazy`, but
 * with a single root `<Suspense>` the first navigation to a not-yet-evaluated
 * screen suspended the ENTIRE app to a full-screen loader while Metro's
 * inlineRequires evaluated that screen's module graph. A per-route boundary
 * confines that to the destination card, so the previously focused screen stays
 * visible underneath and only the incoming card shows a light spinner.
 *
 * Drop-in replacement for `React.lazy(loader)` at the navigator registration
 * site — same loader signature, returns a ready-to-mount component.
 *
 * @param {() => Promise<{default: React.ComponentType<any>}>} loader Dynamic `import()` thunk.
 * @returns {React.ComponentType<any>} A component that renders the lazy screen behind its own Suspense.
 */
export default function lazyScreen(loader) {
    const Lazy = React.lazy(loader);

    const LazyScreen = (props) => (
        <Suspense fallback={<ScreenFallback />}>
            <Lazy {...props} />
        </Suspense>
    );
    LazyScreen.displayName = 'LazyScreen';

    return LazyScreen;
}
